/**
 * Event formatters utilities
 */

import { EVENT_TYPES } from '@/constants/benefitIds'

/**
 * Returns the translated label for event type
 * @param {string} eventType - Event type (EXCLUSIVE_EVENTS or FACTORY_VISIT)
 * @returns {string} Event type label
 */
export const getEventTypeLabel = (eventType) => {
  const EVENT_TYPE_LABELS = {
    [EVENT_TYPES.EXCLUSIVE_EVENTS]: 'Eventos Exclusivos',
    [EVENT_TYPES.FACTORY_VISIT]: 'Visita à Fábrica'
  }

  return EVENT_TYPE_LABELS[eventType] || eventType
}

/**
 * Constants for date formatting
 */
const DATE_CONSTANTS = {
  NOT_INFORMED_LABEL: 'Não informado',
  EXPECTED_PARTS_COUNT: 3,
  SEPARATOR_SLASH: '/',
  SEPARATOR_DASH: '-'
}

/**
 * Validates if a date string is empty or null
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if date is empty/null
 */
const isEmpty = (dateStr) => !dateStr || typeof dateStr !== 'string' || dateStr.trim() === ''

/**
 * Checks if date is already in display format (dd/mm/yyyy)
 * @param {string} dateStr - Date string to check
 * @returns {boolean} True if already formatted
 */
const isAlreadyFormatted = (dateStr) => dateStr.includes(DATE_CONSTANTS.SEPARATOR_SLASH)

/**
 * Validates date parts array
 * @param {string[]} dateParts - Array of date parts
 * @returns {boolean} True if valid
 */
const hasValidDateParts = (dateParts) => {
  if (dateParts.length !== DATE_CONSTANTS.EXPECTED_PARTS_COUNT) {
    return false
  }

  return dateParts.every((part) => part && part.trim() !== '')
}

/**
 * Converts ISO date format (yyyy-mm-dd) to display format (dd/mm/yyyy)
 * @param {string} dateStr - Date in yyyy-mm-dd format
 * @returns {string} Date in dd/mm/yyyy format
 */
const convertIsoToDisplayFormat = (dateStr) => {
  const trimmedDate = dateStr.trim()
  const dateParts = trimmedDate.split(DATE_CONSTANTS.SEPARATOR_DASH)

  if (!hasValidDateParts(dateParts)) {
    return DATE_CONSTANTS.NOT_INFORMED_LABEL
  }

  const [year, month, day] = dateParts
  return `${day}/${month}/${year}`
}

/**
 * Formats a date for display (dd/mm/yyyy)
 * @param {string} dateStr - Date in yyyy-mm-dd or dd/mm/yyyy format
 * @returns {string} Formatted date or 'Não informado'
 */
export const formatDisplayDate = (dateStr) => {
  if (isEmpty(dateStr)) {
    return DATE_CONSTANTS.NOT_INFORMED_LABEL
  }

  if (isAlreadyFormatted(dateStr)) {
    return dateStr
  }

  return convertIsoToDisplayFormat(dateStr)
}

/**
 * Formats time for display
 * @param {string} timeStr - Time in hh:mm format
 * @returns {string} Formatted time or 'Não informado'
 */
export const formatDisplayTime = (timeStr) => {
  if (!timeStr) return 'Não informado'
  return timeStr
}

/**
 * Gets event image preview URL
 * @param {Object} formValues - Form values
 * @param {string} formValues.imageUrl - Saved image URL (existing event)
 * @param {string} formValues.imagePreviewUrl - Temporary preview URL (data:image... or blob:...)
 * @param {string} formValues.image - S3 image key
 * @returns {Object|null} Object with URL, or null
 * @returns {string} url - Image URL
 */
export const getImagePreviewUrl = (formValues) => {
  if (formValues.imageUrl) {
    return { url: formValues.imageUrl }
  }

  if (formValues.imagePreviewUrl) {
    return { url: formValues.imagePreviewUrl }
  }

  return null
}

/**
 * Maps selected brands to their labels
 * @param {Object} brands - Object with selected brands {BOT: true, QDB: false, BLZ: true}
 * @returns {string} String with brands separated by comma
 */
export const getSelectedBrandsLabel = (brands = {}) => {
  const BRAND_LABELS = {
    BOT: 'O Boticário',
    QDB: 'Quem Disse, Berenice?',
    BLZ: 'Beleza na Web',
    EUD: 'Eudora'
  }

  const selectedBrands = Object.entries(brands)
    .filter(([, isSelected]) => isSelected)
    .map(([brandKey]) => BRAND_LABELS[brandKey])
    .filter(Boolean)

  return selectedBrands.length > 0 ? selectedBrands.join(', ') : 'Nenhuma marca selecionada'
}
